/**
 * AdminPosRegisters — cashier-facing register session + Z-report UI.
 *
 * Route: /admin/pos/registers (admin auth).
 *
 *   • Top card: current open session (if any), with the live Z-report.
 *   • Open form: register_id + opening_float → POST /open.
 *   • Close dialog: count drawer → POST /close → renders the final Z-report.
 *   • Bottom list: past closed sessions for the calling cashier (last 30).
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Wallet, Calculator, Lock, Unlock, RefreshCw, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const ZReportBlock = ({ z }) => {
  if (!z) return null;
  const cur = z.currency || 'USD';
  const fmt = (n) => `${cur} ${Number(n || 0).toFixed(2)}`;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="z-report">
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500">Opening float</div>
          <div className="text-xl text-slate-100 tabular-nums">{fmt(z.opening_float)}</div>
        </CardContent>
      </Card>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500">Sales · count</div>
          <div className="text-xl text-emerald-300 tabular-nums">
            {fmt(z.sales_total)} <span className="text-xs text-slate-500">· {z.sales_count}</span>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500">Refunds</div>
          <div className="text-xl text-rose-300 tabular-nums">
            {fmt(z.refund_total)} <span className="text-xs text-slate-500">· {z.refund_count}</span>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500">Expected drawer</div>
          <div className="text-xl text-amber-300 tabular-nums">{fmt(z.expected_cash_total)}</div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800 col-span-2 md:col-span-4">
        <CardContent className="p-3">
          <div className="text-[10px] uppercase text-slate-500 mb-1.5">By payment method</div>
          {(z.by_payment_method || []).length === 0 && (
            <div className="text-xs text-slate-500">No sales in this session.</div>
          )}
          {(z.by_payment_method || []).map(b => (
            <div key={b.method} className="flex justify-between text-sm border-b border-slate-800/60 py-1">
              <span className="capitalize text-slate-200">{b.method}</span>
              <span className="tabular-nums text-slate-300">{fmt(b.total)} <span className="text-xs text-slate-500">× {b.count}</span></span>
            </div>
          ))}
        </CardContent>
      </Card>

      {z.closing_count !== null && z.closing_count !== undefined && (
        <Card className={`col-span-2 md:col-span-4 border ${z.discrepancy === 0 ? 'border-emerald-500/40' : 'border-amber-500/40'}`}>
          <CardContent className="p-3 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase text-slate-500">Counted</div>
              <div className="text-xl text-slate-100 tabular-nums">{fmt(z.closing_count)}</div>
            </div>
            <div className={`text-right ${z.discrepancy > 0 ? 'text-emerald-300' : z.discrepancy < 0 ? 'text-rose-300' : 'text-slate-400'}`}>
              <div className="text-[10px] uppercase">Discrepancy</div>
              <div className="text-2xl tabular-nums">
                {z.discrepancy >= 0 ? '+' : ''}{fmt(z.discrepancy)}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const AdminPosRegisters = () => {
  const qc = useQueryClient();
  const [openForm, setOpenForm] = useState({ register_id: '', opening_float: 0, currency: 'USD' });
  const [closing, setClosing] = useState({ open: false, closing_count: 0, notes: '' });

  const { data: current, refetch: refetchCurrent } = useQuery({
    queryKey: ['register-current'],
    queryFn: async () => (await axios.get(`${API}/api/pos/registers/current`, { headers: authH() })).data,
    refetchInterval: 15000,
  });

  const { data: zReport, refetch: refetchZ } = useQuery({
    queryKey: ['register-z', current?.id],
    queryFn: async () => (await axios.get(
      `${API}/api/pos/registers/${current.id}/z-report`, { headers: authH() }
    )).data,
    enabled: !!current?.id,
    refetchInterval: 8000,
  });

  const { data: history = [] } = useQuery({
    queryKey: ['register-history'],
    queryFn: async () => (await axios.get(
      `${API}/api/pos/registers?status=closed&limit=30`, { headers: authH() }
    )).data,
  });

  const openMut = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/pos/registers/open`, openForm, { headers: authH() }
    )).data,
    onSuccess: () => {
      toast.success('Register opened');
      qc.invalidateQueries({ queryKey: ['register-current'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Open failed'),
  });

  const closeMut = useMutation({
    mutationFn: async () => (await axios.post(
      `${API}/api/pos/registers/${current.id}/close`,
      { closing_count: Number(closing.closing_count), notes: closing.notes },
      { headers: authH() }
    )).data,
    onSuccess: (data) => {
      const d = Number(data.discrepancy);
      if (d === 0) toast.success('Drawer balanced — session closed.');
      else if (d > 0) toast.warning(`Over by ${data.currency} ${d.toFixed(2)} — session closed.`);
      else          toast.error(`Short by ${data.currency} ${Math.abs(d).toFixed(2)} — session closed.`);
      setClosing({ open: false, closing_count: 0, notes: '' });
      qc.invalidateQueries({ queryKey: ['register-current'] });
      qc.invalidateQueries({ queryKey: ['register-history'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Close failed'),
  });

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Wallet className="h-6 w-6 text-indigo-400" /> Register Sessions
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Open the drawer with a float, run sales, close & reconcile against the expected total (Z-report).
            </p>
          </div>
          <Button variant="outline" size="sm"
                   onClick={() => { refetchCurrent(); refetchZ(); }}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </header>

        {/* Current session card */}
        {current ? (
          <Card className="bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/5 border-indigo-400/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center justify-between">
                <span className="inline-flex items-center gap-2">
                  <Unlock className="h-5 w-5 text-emerald-300" />
                  Register <code className="text-base bg-slate-800/60 px-2 py-0.5 rounded ml-2">{current.register_id}</code>
                </span>
                <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-400/40">OPEN</Badge>
              </CardTitle>
              <div className="text-xs text-slate-400 mt-1">
                Cashier · {current.cashier_name} · Opened {new Date(current.opened_at).toLocaleString()}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <ZReportBlock z={zReport} />
              <Button onClick={() => setClosing({ ...closing, open: true,
                            closing_count: zReport?.expected_cash_total || 0 })}
                       className="w-full bg-rose-600 hover:bg-rose-700"
                       data-testid="register-close-btn">
                <Lock className="h-4 w-4 mr-2" /> Close session & count drawer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-900/60 border-slate-800">
            <CardHeader>
              <CardTitle className="text-base">No open register</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Register ID</Label>
                  <Input value={openForm.register_id}
                         onChange={e => setOpenForm({ ...openForm, register_id: e.target.value })}
                         className="bg-slate-800/60 border-slate-700"
                         placeholder="e.g. cafe-01"
                         data-testid="register-open-id" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Opening float</Label>
                  <Input type="number" step="0.01" value={openForm.opening_float}
                         onChange={e => setOpenForm({ ...openForm, opening_float: e.target.value })}
                         className="bg-slate-800/60 border-slate-700"
                         data-testid="register-open-float" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Currency</Label>
                  <Input value={openForm.currency}
                         onChange={e => setOpenForm({ ...openForm, currency: e.target.value.toUpperCase() })}
                         className="bg-slate-800/60 border-slate-700"
                         maxLength={3} />
                </div>
              </div>
              <Button onClick={() => openMut.mutate()}
                       disabled={!openForm.register_id || openMut.isPending}
                       data-testid="register-open-btn">
                <Unlock className="h-4 w-4 mr-2" />
                {openMut.isPending ? 'Opening…' : 'Open register'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* History */}
        <Card className="bg-slate-900/60 border-slate-800">
          <CardHeader>
            <CardTitle className="text-sm">Recent closed sessions ({history.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
                <tr>
                  <th className="text-left px-4 py-2">Register</th>
                  <th className="text-left px-4 py-2">Cashier</th>
                  <th className="text-left px-4 py-2 hidden md:table-cell">Opened</th>
                  <th className="text-right px-4 py-2">Float</th>
                  <th className="text-right px-4 py-2">Counted</th>
                  <th className="text-right px-4 py-2">Discrepancy</th>
                </tr>
              </thead>
              <tbody data-testid="register-history">
                {history.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                    No closed sessions yet.
                  </td></tr>
                )}
                {history.map(h => (
                  <tr key={h.id} className="border-b border-slate-800/50">
                    <td className="px-4 py-2 text-slate-200 font-mono text-xs">{h.register_id}</td>
                    <td className="px-4 py-2 text-slate-400 text-xs">{h.cashier_name}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell">
                      {new Date(h.opened_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-300">
                      {(h.currency || 'USD')} {Number(h.opening_float).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-300">
                      {(h.currency || 'USD')} {Number(h.closing_count || 0).toFixed(2)}
                    </td>
                    <td className={`px-4 py-2 text-right tabular-nums ${
                      h.discrepancy === 0 ? 'text-slate-400'
                      : h.discrepancy > 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                      {h.discrepancy === 0 ? '—' : `${h.discrepancy > 0 ? '+' : ''}${Number(h.discrepancy).toFixed(2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Close dialog */}
        <Dialog open={closing.open} onOpenChange={(o) => setClosing({ ...closing, open: o })}>
          <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
            <DialogHeader>
              <DialogTitle>Close register & count drawer</DialogTitle>
              <DialogDescription className="text-xs text-slate-500">
                Expected · {zReport?.currency || 'USD'} {Number(zReport?.expected_cash_total || 0).toFixed(2)}.
                Enter the amount you physically counted in the drawer.
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label className="text-xs text-slate-500">Counted amount</Label>
              <Input type="number" step="0.01" value={closing.closing_count}
                     onChange={e => setClosing({ ...closing, closing_count: e.target.value })}
                     className="bg-slate-800/60 border-slate-700 text-2xl tabular-nums"
                     data-testid="register-close-count" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Notes (optional)</Label>
              <Textarea value={closing.notes}
                         onChange={e => setClosing({ ...closing, notes: e.target.value })}
                         className="bg-slate-800/60 border-slate-700"
                         placeholder="Anything to flag for the manager…" rows={2} />
            </div>
            {zReport && (
              <div className={`rounded-md p-3 text-sm flex items-start gap-2 ${
                Number(closing.closing_count) - zReport.expected_cash_total === 0
                  ? 'bg-emerald-500/10 text-emerald-200 border border-emerald-500/30'
                  : 'bg-amber-500/10 text-amber-200 border border-amber-500/30'
              }`}>
                {Number(closing.closing_count) - zReport.expected_cash_total === 0
                  ? <CheckCircle2 className="h-4 w-4 mt-0.5" />
                  : <AlertTriangle className="h-4 w-4 mt-0.5" />}
                <div>
                  Discrepancy will be <strong>
                    {((Number(closing.closing_count) - zReport.expected_cash_total) >= 0 ? '+' : '')}
                    {(Number(closing.closing_count) - zReport.expected_cash_total).toFixed(2)}
                  </strong>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setClosing({ ...closing, open: false })}>Cancel</Button>
              <Button onClick={() => closeMut.mutate()} disabled={closeMut.isPending}
                       className="bg-rose-600 hover:bg-rose-700"
                       data-testid="register-close-confirm">
                <Calculator className="h-4 w-4 mr-2" />
                {closeMut.isPending ? 'Closing…' : 'Confirm close'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPosRegisters;
